import { ReceiveUserMessageRequest } from "@models/ChatAIModels";
import { getToken, setToken } from "@stores/authStore";
import ChatAIApi from "api/ChatAIApi";
import React, { useEffect, useRef, useState } from "react";

type ChatMessage = {
  id: number;
  role: "user" | "assistant";
  content: string;
  url?: string | null;
};

interface AiChatBoxProps {
  serviceId: string;
  serviceToken: string;
}
const AiChatBox: React.FC<AiChatBoxProps> = ({ serviceId, serviceToken }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);

  const StartNewConversation = async () => {
    setLoading(true);
    const response = await ChatAIApi.startNewSession(serviceId, serviceToken, 1);
    if (response) {
      setMessages([
        {
          id: 1,
          role: "assistant",
          content: response.welcomeMessage,
        },
      ]);
      setToken(response.jwtToken);
    }
    setLoading(false);
  };

  useEffect(() => {
    StartNewConversation();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      role: "user",
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    const reqData: ReceiveUserMessageRequest = {
      jwt: getToken() ?? "",
      message: input,
    };

    const response = await ChatAIApi.receiveUserMessageResponse(reqData);
    if (response && response.content) {
      const aiMessage: ChatMessage = {
        id: Date.now(),
        role: "assistant",
        content: response.content.assistantResponse,
        url: response.content.assistantResponse.match(/https?:\/\/[^\s]+/)?.[0],
      };
      setMessages((prev) => [...prev, aiMessage]);
    }

    setLoading(false);
  };

  return (
    <div className="ai-chat-container compact">
      <div className="ai-chat-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`ai-message ${msg.role}`}>
            <div className="ai-avatar">{msg.role === "assistant" ? "👩‍💼" : "🧑"}</div>
            <div className="ai-bubble">
              <div>{msg.content}</div>

              {msg.url && (
                <div className="ai-link-wrapper">
                  <a href={msg.url} target="_blank" rel="noopener noreferrer" className="ai-link">
                    {msg.url}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="ai-message assistant">
            <div className="ai-avatar">👩‍💼</div>
            <div className="ai-bubble typing">
              Yazıyor<span>.</span>
              <span>.</span>
              <span>.</span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="ai-chat-input">
        <input
          type="text"
          placeholder="Sorunuzu yazın..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button onClick={handleSend} disabled={loading}>
          Gönder
        </button>
      </div>
      <style>{`
      .ai-chat-container {
        display: flex;
        flex-direction: column;
        height: 100vh;
        border-radius: 10px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
      }

      .ai-chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 12px;
      }

      .ai-message {
        display: flex;
        align-items: flex-start;
        margin-bottom: 10px;
      }

      .ai-message.user {
        flex-direction: row-reverse;
      }

      .ai-avatar {
        font-size: 18px;
        margin: 0 6px;
        flex-shrink: 0;
      }

      .ai-bubble {
        max-width: 75%;
        padding: 8px 10px;
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.45;
        word-break: break-word;
        white-space: pre-wrap;
      }

      .ai-message.assistant .ai-bubble {
        background: #ffffff;
        border: 1px solid #e5e7eb;
      }

      .ai-message.user .ai-bubble {
        background: linear-gradient(135deg, #4f46e5, #6366f1);
        color: #ffffff;
      }


      .ai-chat-input {
        display: flex;
        gap: 6px;
        padding: 8px;
        border-top: 1px solid #e5e7eb;
        background: #ffffff;
      }

      .ai-chat-input input {
        flex: 1;
        height: 36px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 0 10px;
        font-size: 13px;
      }

      .ai-chat-input button {
        height: 36px;
        padding: 0 14px;
        border-radius: 8px;
        border: none;
        background: #4f46e5;
        color: #ffffff;
        font-size: 13px;
        white-space: nowrap;
      }

      .ai-chat-input button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }


      .typing span {
        animation: blink 1.4s infinite both;
      }

      .typing span:nth-child(2) {
        animation-delay: 0.2s;
      }
      .typing span:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes blink {
        0% {
          opacity: 0.2;
        }
        20% {
          opacity: 1;
        }
        100% {
          opacity: 0.2;
        }
      }
      
      .ai-link-wrapper {
        margin-top: 6px;
      }

      .ai-link {
        font-weight: bold;
        color: #2563eb;
        word-break: break-all;
        text-decoration: underline;
        font-size: 13px;
      }`}</style>
    </div>
  );
};

export default AiChatBox;
